import React from 'react';
import { Link } from 'react-router-dom';
import {
  Ticket,
  MessageSquare,
  Bell,
  BarChart3,
  ShieldCheck,
  Zap,
  Users,
  FolderKanban,
  Clock,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: Ticket,
      title: 'Ticket Management',
      description:
        'Create, assign, prioritize, and track tickets through their entire lifecycle. Custom statuses and priorities keep every issue moving toward resolution.',
    },
    {
      icon: MessageSquare,
      title: 'Real-Time Chat',
      description:
        'Chat directly inside each ticket with customers and teammates. Live updates powered by web sockets mean no more refreshing or waiting for email replies.',
    },
    {
      icon: Zap,
      title: 'Smart Escalation',
      description:
        'Configurable escalation rules automatically raise the alarm when tickets breach response or resolution deadlines — before customers have to ask twice.',
    },
    {
      icon: Users,
      title: 'Roles & Permissions',
      description:
        'Fine-grained role-based access control. Admins, staff levels, and customers each see exactly what they need — nothing more, nothing less.',
    },
    {
      icon: FolderKanban,
      title: 'Categories & Routing',
      description:
        'Organize tickets by category so the right issues land with the right team. Categories keep queues clean and reporting meaningful.',
    },
    {
      icon: Bell,
      title: 'Notifications',
      description:
        'In-app notifications keep everyone informed the moment a ticket is assigned, updated, escalated, or resolved.',
    },
    {
      icon: BarChart3,
      title: 'Staff Reports & Analytics',
      description:
        'Dashboards and staff performance reports show resolution times, workloads, and trends — so managers can coach with data, not guesswork.',
    },
    {
      icon: Clock,
      title: 'Audit Logs',
      description:
        'A complete history of every ticket action. Know exactly who did what and when, for accountability and compliance.',
    },
    {
      icon: ShieldCheck,
      title: 'Secure by Design',
      description:
        'Authentication, protected routes, and permission checks on every sensitive action keep your incident data safe.',
    },
  ];

  const highlights = [
    'Role-based dashboards for admins, staff, and customers',
    'Ticket chat with file-friendly, real-time messaging',
    'Automatic escalation with configurable thresholds',
    'Detailed staff performance reporting',
    'System configuration without touching code',
  ];

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
            Powerful <span className="text-primary-600">Features</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Everything your team needs to capture, track, and resolve incidents — built into
            one fast, modern platform.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 hover:shadow-md hover:border-primary-200 transition-all"
              >
                <div className="h-12 w-12 rounded-xl bg-primary-50 flex items-center justify-center mb-5">
                  <Icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Highlights */}
        <div className="mt-16 bg-white rounded-2xl shadow-sm border border-gray-200 p-8 sm:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Built for the whole team
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Whether you're a customer reporting an issue, an agent working the queue, or an
                admin overseeing the whole operation — the system adapts to your role and shows
                you what matters.
              </p>
              <ul className="space-y-3">
                {highlights.map((item) => (
                  <li key={item} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 text-center">
              <BarChart3 className="h-16 w-16 text-primary-600 mx-auto mb-4" />
              <p className="text-primary-900 font-semibold text-lg">
                Data-driven support, out of the box
              </p>
              <p className="mt-2 text-primary-700 text-sm">
                Dashboards, reports, and logs — no extra setup required.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-900">See it in action</h2>
          <p className="mt-3 text-gray-600">
            Create an account and raise your first ticket in under a minute.
          </p>
          <Link
            to="/register"
            className="mt-6 inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-all hover:shadow-lg"
          >
            Get Started Free
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Features;
